/**
 * Analüütik, the Ask page (/ask, a thread at /ask/<id>; the Ask page sketch): the person's questions on
 * the left, newest first; the thread's questions, each as it was answered (AskWork); the question
 * being answered live below them; the next question's box. A question asked here without a thread
 * starts one, and the address follows it. The button on every other page (AskButton.tsx) opens a
 * thread here.
 *
 * A thread can be archived from its row: it leaves the list for the archive under it, still opens
 * from there, and comes back when restored or asked in again. Reversible, so nothing asks first.
 *
 * Before the first question the box is in the middle of the page, under the orb and the title,
 * the examples under it. Asked, it glides to the bottom (from where it was to where it is: a FLIP
 * with the Web Animations API, none when the reader asks for no motion) and stays there while the
 * thread scrolls above it.
 */
import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import type { FormEvent, KeyboardEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { Button, IconButton, icons } from '@/kit'
import { cx } from '@/kit/cx'
import { ASK_PATH, fetchThread, fetchThreads, setThreadArchived } from './askApi'
import type { AskThreadSummary } from './askApi'
import { runOfItem } from './askRun'
import type { AskRun } from './askRun'
import { useAsk } from './AskProvider'
import { AskWork } from './AskWork'
import { Orb } from './Mark'
import './i18n'

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

function startOfToday(): number {
    const d = new Date()
    d.setHours(0, 0, 0, 0)
    return d.getTime()
}

function Question({ run }: { run: AskRun }) {
    const { t } = useTranslation()
    const ask = useAsk()
    const page = ask?.pageTitle(run.page) ?? null
    return (
        <div className="flex flex-col items-end gap-[6px]">
            {page && <div className="text-[12px] leading-[16px] font-normal text-[var(--text-weaker)]">{t('ask.askedOn', { page })}</div>}
            <div className="max-w-[560px] rounded-[12px] bg-[var(--surface-header)] px-[14px] py-[10px] text-[15px] leading-[21px] font-[550] whitespace-pre-wrap text-[var(--text-default)]">{run.question}</div>
        </div>
    )
}

/** The question's box: Enter asks, Shift+Enter is a new line. `context` (what it goes by) beside the button, when given. */
export function Composer({ onAsk, placeholder, context, autoFocus = false, busy = false }: { onAsk: (question: string) => void; placeholder: string; context?: string; autoFocus?: boolean; busy?: boolean }) {
    const { t } = useTranslation()
    const id = useId()
    const [text, setText] = useState('')
    const submit = (e?: FormEvent) => {
        e?.preventDefault()
        const question = text.trim()
        if (!question || busy) return
        onAsk(question)
        setText('')
    }
    const onKey = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
            e.preventDefault()
            submit()
        }
    }
    return (
        <form onSubmit={submit} className="flex flex-col gap-[8px] rounded-[12px] border border-solid border-[var(--border-strong)] bg-[var(--surface-raised)] py-[10px] pr-[10px] pl-[14px] shadow-[0_1px_2px_rgba(16,16,19,0.04)] focus-within:shadow-[var(--focus-field)]">
            <label className="sr-only" htmlFor={id}>
                {t('ask.inputLabel')}
            </label>
            <textarea
                id={id}
                rows={2}
                value={text}
                maxLength={2000}
                autoFocus={autoFocus}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={onKey}
                placeholder={placeholder}
                className="resize-none border-0 bg-transparent text-[14px] leading-[20px] font-normal text-[var(--text-default)] outline-none"
            />
            <div className="flex flex-wrap items-center justify-between gap-[8px]">
                {context ? <span className="inline-flex items-center gap-[6px] rounded-[6px] bg-[var(--surface-header)] px-[8px] py-[4px] text-[12px] leading-[16px] font-normal text-[var(--text-weak)]">{context}</span> : <span />}
                <Button type="submit" variant="primary" disabled={!text.trim() || busy}>
                    {t('ask.send')}
                </Button>
            </div>
        </form>
    )
}

const GROUP_LABEL = 'px-[8px] pb-[4px] text-[11px] leading-[14px] font-[650] tracking-[0.06em] text-[var(--text-weaker)] uppercase'

/** A thread in the list, and beside it what can be done to it: on hover, always where nothing hovers. */
function ThreadRow({ thread, current, action }: { thread: AskThreadSummary; current: string | null; action: { icon: string; label: string; run: () => void } }) {
    const navigate = useNavigate()
    return (
        <div data-testid="ask-thread" className="group/thread relative">
            <button
                type="button"
                onClick={() => navigate(`${ASK_PATH}/${thread.thread_id}`)}
                aria-current={thread.thread_id === current ? 'page' : undefined}
                className={cx(
                    'w-full cursor-pointer rounded-[6px] border-0 py-[8px] pr-[36px] pl-[8px] text-left text-[13.5px] leading-[18px] pointer-coarse:pr-[44px]',
                    thread.thread_id === current ? 'bg-[var(--surface-raised)] font-[600] text-[var(--text-default)] shadow-[0_0_0_1px_#0000000d]' : 'bg-transparent font-normal text-[var(--text-weak)] hover:bg-[var(--interaction-hover)]',
                )}
            >
                <span className="line-clamp-2">{thread.title}</span>
            </button>
            <span className="absolute top-px right-px opacity-0 group-focus-within/thread:opacity-100 group-hover/thread:opacity-100 hover-none:opacity-100">
                <IconButton icon={action.icon} label={action.label} onClick={action.run} />
            </span>
        </div>
    )
}

function ThreadList({ threads, archived, current, onNew, onArchive, onShowArchived }: { threads: AskThreadSummary[]; archived: number; current: string | null; onNew: () => void; onArchive: (id: string) => void; onShowArchived: () => void }) {
    const { t } = useTranslation()
    const today = startOfToday()
    const groups = [
        { label: t('ask.today'), items: threads.filter((x) => new Date(x.last_at).getTime() >= today) },
        { label: t('ask.earlier'), items: threads.filter((x) => new Date(x.last_at).getTime() < today) },
    ].filter((g) => g.items.length > 0)
    return (
        <nav aria-label={t('ask.threads')} className="flex flex-col gap-[16px]">
            <Button variant="primary" onClick={onNew} className="w-full">
                {t('ask.newQuestion')}
            </Button>
            {groups.length === 0 && <p className="m-0 px-[8px] text-[13px] leading-[18px] font-normal text-[var(--text-weaker)]">{t('ask.noThreads')}</p>}
            {groups.map((group) => (
                <div key={group.label} className="flex flex-col gap-[2px]">
                    <div className={GROUP_LABEL}>{group.label}</div>
                    {group.items.map((thread) => (
                        <ThreadRow key={thread.thread_id} thread={thread} current={current} action={{ icon: icons.archive16, label: t('ask.archive'), run: () => onArchive(thread.thread_id) }} />
                    ))}
                </div>
            ))}
            {archived > 0 && (
                <Button variant="ghost" onClick={onShowArchived} className="self-start">
                    {t('ask.archivedCount', { n: archived })}
                </Button>
            )}
        </nav>
    )
}

function ArchivedList({ threads, current, onBack, onRestore }: { threads: AskThreadSummary[]; current: string | null; onBack: () => void; onRestore: (id: string) => void }) {
    const { t } = useTranslation()
    return (
        <nav aria-label={t('ask.archived')} className="flex flex-col gap-[16px]">
            <Button variant="ghost" onClick={onBack} className="self-start">
                {t('ask.backToThreads')}
            </Button>
            <div className="flex flex-col gap-[2px]">
                <div className={GROUP_LABEL}>{t('ask.archived')}</div>
                {threads.length === 0 && <p className="m-0 px-[8px] text-[13px] leading-[18px] font-normal text-[var(--text-weaker)]">{t('ask.noArchived')}</p>}
                {threads.map((thread) => (
                    <ThreadRow key={thread.thread_id} thread={thread} current={current} action={{ icon: icons.clockCounterClockwise16, label: t('ask.restore'), run: () => onRestore(thread.thread_id) }} />
                ))}
            </div>
        </nav>
    )
}

export function AskPage() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const params = useParams()
    const ask = useAsk()
    const rest = params['*'] ?? ''
    const threadId = UUID.test(rest) ? rest : null
    const client = useQueryClient()
    const threads = useQuery({ queryKey: ['ask-threads'], queryFn: ({ signal }) => fetchThreads(false, signal) })
    const archived = useQuery({ queryKey: ['ask-threads', 'archived'], queryFn: ({ signal }) => fetchThreads(true, signal) })
    const [showArchived, setShowArchived] = useState(false)
    const thread = useQuery({ queryKey: ['ask-thread', threadId], queryFn: ({ signal }) => fetchThread(threadId!, signal), enabled: threadId !== null })
    const run = ask?.run ?? null
    const items = threadId ? (thread.data?.items ?? []) : []

    // A question asked here without a thread got one: the address follows it (however soon it ended).
    const [startedHere, setStartedHere] = useState<number | null>(null)
    useEffect(() => {
        if (!threadId && run && run.key === startedHere && run.threadId) navigate(`${ASK_PATH}/${run.threadId}`, { replace: true })
    }, [threadId, run, startedHere, navigate])

    // The live question belongs here while its thread is this one. Once the thread lists it, it takes
    // that question's place: one copy, and an answer still typing in isn't swapped for the list's.
    const liveHere = run && (run.threadId === threadId || (!threadId && !run.threadId)) ? run : null
    const listed = liveHere !== null && items.some((i) => i.question_id === liveHere.questionId)
    const endRef = useRef<HTMLDivElement>(null)
    const lastKey = liveHere ? liveHere.key : (items.at(-1)?.question_id ?? 0)
    useEffect(() => {
        endRef.current?.scrollIntoView?.({ block: 'end', behavior: 'smooth' })
    }, [lastKey])

    const onAsk = (question: string) => {
        if (!ask) return
        const key = ask.ask({ question, page: null, state: {}, threadId })
        if (!threadId) setStartedHere(key)
    }
    const onNew = () => {
        ask?.clear()
        navigate(ASK_PATH)
    }
    const archiving = useMutation({
        mutationFn: ({ id, archive }: { id: string; archive: boolean }) => setThreadArchived(id, archive),
        onSuccess: (_, { id, archive }) => {
            void client.invalidateQueries({ queryKey: ['ask-threads'] })
            void client.invalidateQueries({ queryKey: ['ask-thread', id] })
            // The open thread leaves the screen along with its row.
            if (archive && id === threadId) onNew()
        },
    })
    const spend = ask?.status?.budget
    const empty = !threadId && !liveHere

    // The box's glide from the middle to the bottom: where it was, taken as the question is asked.
    const composerRef = useRef<HTMLDivElement>(null)
    const fromRect = useRef<DOMRect | null>(null)
    const wasEmpty = useRef(empty)
    useLayoutEffect(() => {
        const was = wasEmpty.current
        wasEmpty.current = empty
        const from = fromRect.current
        const el = composerRef.current
        fromRect.current = null
        if (!was || empty || !from || !el || typeof el.animate !== 'function') return
        if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return
        const to = el.getBoundingClientRect()
        if (!to.width) return
        el.animate(
            [
                { transform: `translate(${from.left - to.left}px, ${from.top - to.top}px) scale(${from.width / to.width})`, transformOrigin: 'top left' },
                { transform: 'none', transformOrigin: 'top left' },
            ],
            { duration: 480, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' },
        )
    }, [empty])
    const askFromHere = (question: string) => {
        if (empty) fromRect.current = composerRef.current?.getBoundingClientRect() ?? null
        onAsk(question)
    }
    const examples = (ask?.status?.examples ?? []).filter((e) => e.page === null).slice(0, 4)
    const liveBlock = (live: NonNullable<typeof run>) => (
        <div key={`live-${live.key}`} className="flex flex-col gap-[18px]">
            <Question run={live} />
            <AskWork run={live} live onAsk={onAsk} />
        </div>
    )

    return (
        <div className="flex w-full flex-wrap" data-testid="ask-page">
            <aside className="box-border flex flex-[1_1_240px] flex-col gap-[16px] px-[16px] py-[20px] sm:max-w-[280px] max-sm:border-b max-sm:border-solid max-sm:border-[var(--border-default)]">
                {showArchived ? (
                    <ArchivedList threads={archived.data ?? []} current={threadId} onBack={() => setShowArchived(false)} onRestore={(id) => archiving.mutate({ id, archive: false })} />
                ) : (
                    <ThreadList threads={threads.data ?? []} archived={archived.data?.length ?? 0} current={threadId} onNew={onNew} onArchive={(id) => archiving.mutate({ id, archive: true })} onShowArchived={() => setShowArchived(true)} />
                )}
                {archiving.isError && <p role="alert" className="m-0 px-[8px] text-[13px] leading-[18px] text-[var(--text-weak)]">{t('ask.archiveFailed')}</p>}
                {threads.isError && (
                    <div className="flex flex-col items-start gap-[8px] px-[8px]">
                        <p className="m-0 text-[13px] leading-[18px] text-[var(--text-weak)]">{t('ask.loadFailed')}</p>
                        <Button variant="secondary" onClick={() => void threads.refetch()}>
                            {t('ask.retry')}
                        </Button>
                    </div>
                )}
                {spend && spend.limit_eur > 0 && <p className="m-0 mt-auto px-[8px] text-[12px] leading-[17px] font-normal text-[var(--text-weaker)]">{t('ask.budget', { share: `${Math.round((100 * spend.spent_eur) / spend.limit_eur)} %` })}</p>}
            </aside>
            <section
                className={cx(
                    // The screen's height under the top bar: the box is in its middle, or at its bottom.
                    'box-border flex min-h-[calc(100dvh-140px)] min-w-0 flex-[999_1_640px] flex-col gap-[28px] border-l border-solid border-[var(--border-default)] bg-[var(--surface-raised)] px-[var(--layout-page-padding-x)] pt-[28px] max-sm:border-l-0',
                    // Before the first question: the orb, the title and the box in the middle of the page.
                    empty ? 'justify-center pb-[56px]' : 'pb-[20px]',
                )}
            >
                {empty && (
                    <div className="flex flex-col items-center gap-[14px] text-center">
                        <Orb size={56} calm />
                        {/* Positioned, so they paint over the orb's halo. */}
                        <h1 className="relative m-0 text-[28px] leading-[1.21] font-[550] tracking-[-0.42px] text-[var(--text-default)]">{t('ask.emptyTitle')}</h1>
                        <p className="relative m-0 max-w-[560px] text-[14px] leading-[20px] font-normal text-[var(--text-weak)]">{t('ask.emptyHint')}</p>
                    </div>
                )}
                {threadId && thread.isError && <p className="m-0 text-[14px] text-[var(--text-weak)]">{t('ask.loadFailed')}</p>}
                {threadId && thread.data?.archived && (
                    <div data-testid="ask-archived" className="flex flex-wrap items-center justify-between gap-[12px] rounded-[8px] bg-[var(--surface-header)] px-[12px] py-[8px] text-[13px] leading-[18px] font-normal text-[var(--text-weak)]">
                        <span>{t('ask.archivedNote')}</span>
                        <Button variant="secondary" onClick={() => archiving.mutate({ id: threadId, archive: false })}>
                            {t('ask.restore')}
                        </Button>
                    </div>
                )}
                {/* One list, so the live question keeps its key (and its card) when the thread lists it. */}
                {[
                    ...items.map((item) => {
                        if (liveHere && item.question_id === liveHere.questionId) return liveBlock(liveHere)
                        const done = runOfItem(item)
                        return (
                            <div key={item.question_id} className="flex flex-col gap-[18px]">
                                <Question run={done} />
                                <AskWork run={done} live={false} onAsk={onAsk} />
                            </div>
                        )
                    }),
                    ...(liveHere && !listed ? [liveBlock(liveHere)] : []),
                ]}
                <div
                    ref={composerRef}
                    data-testid="ask-composer"
                    className={cx(
                        'z-10 w-full',
                        // Asked: at the bottom of the screen (pushed down while the thread is short), the
                        // thread scrolling under a short fade once it is long.
                        empty ? 'relative max-w-[720px] self-center' : 'sticky bottom-0 mt-auto bg-[linear-gradient(to_bottom,transparent,var(--surface-raised)_20px)] pt-[20px] pb-[16px]',
                    )}
                >
                    <Composer onAsk={askFromHere} placeholder={threadId ? t('ask.placeholderNext') : t('ask.placeholder')} context={t('ask.context.all')} autoFocus={empty} busy={run?.status === 'running'} />
                </div>
                {empty && examples.length > 0 && (
                    <div className="flex max-w-[720px] flex-wrap justify-center gap-[8px] self-center">
                        {examples.map((e) => (
                            <button key={e.text} type="button" onClick={() => askFromHere(e.text)} className="press min-h-[32px] cursor-pointer rounded-full border border-solid border-[var(--border-strong)] bg-[var(--surface-raised)] px-[12px] py-[5px] text-[13px] leading-[18px] font-[550] text-[var(--text-default)] hover:bg-[var(--interaction-hover)]">
                                {e.text}
                            </button>
                        ))}
                    </div>
                )}
                <div ref={endRef} />
            </section>
        </div>
    )
}
